import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Linkedin, Mail, Phone, MapPin } from 'lucide-react';

const Footer = () => {
  return (
    <footer className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* About Section */}
          <div className="col-span-1">
            <div className="flex items-center space-x-3 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_drive-next-gen/artifacts/0odhl9q2_cda-logo.png" 
                alt="Cricket Drive Logo" 
                className="h-16 w-16 object-contain"
              />
              <div>
                <h3 className="text-lg font-bold">Cricket Drive</h3>
                <p className="text-xs text-green-400">Youth Cricket Academy</p>
              </div>
            </div>
            <p className="text-gray-300 text-sm leading-relaxed">
              Building the next generation of cricket stars through expert coaching and comprehensive development programs.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-green-400">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-gray-300 hover:text-green-400 transition-colors text-sm">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-gray-300 hover:text-green-400 transition-colors text-sm">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/team" className="text-gray-300 hover:text-green-400 transition-colors text-sm">
                  Our Team
                </Link>
              </li>
              <li>
                <Link to="/programs?tab=pricing" className="text-gray-300 hover:text-green-400 transition-colors text-sm">
                  Programs & Pricing
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-gray-300 hover:text-green-400 transition-colors text-sm">
                  FAQs
                </Link>
              </li>
            </ul>
          </div>

          {/* Programs */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-green-400">Programs</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/videos" className="text-gray-300 hover:text-green-400 transition-colors text-sm">
                  Training Videos
                </Link>
              </li>
              <li>
                <Link to="/book" className="text-gray-300 hover:text-green-400 transition-colors text-sm">
                  Book Session
                </Link>
              </li>
              <li>
                <Link to="/gallery" className="text-gray-300 hover:text-green-400 transition-colors text-sm">
                  Gallery
                </Link>
              </li>
              <li>
                <Link to="/testimonials" className="text-gray-300 hover:text-green-400 transition-colors text-sm">
                  Testimonials
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-300 hover:text-green-400 transition-colors text-sm">
                  Contact Us
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-green-400">Contact Us</h4>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-300">
                  810 Center St<br />
                  Apex, NC 27517
                </span>
              </li>
              <li className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-green-400" />
                <a href="tel:+19193356849" className="text-sm text-gray-300 hover:text-green-400 transition-colors">
                  (919) 335-6849
                </a>
              </li>
              <li className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-green-400" />
                <a href="mailto:info@cricketdrive.org" className="text-sm text-gray-300 hover:text-green-400 transition-colors">
                  info@cricketdrive.org
                </a>
              </li>
            </ul>

            {/* Social Media */}
            <div className="flex space-x-3 mt-6">
              <a
                href="https://www.facebook.com/cricketdriveacademy"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow Cricket Drive on Facebook"
                data-testid="footer-facebook-link"
                className="w-10 h-10 bg-slate-800 hover:bg-[#1877F2] rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://www.instagram.com/_cricket_drive_academy_nc/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow Cricket Drive on Instagram"
                data-testid="footer-instagram-link"
                className="w-10 h-10 bg-slate-800 hover:bg-gradient-to-br hover:from-[#E1306C] hover:to-[#F77737] rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://www.linkedin.com/company/cricket-drive/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow Cricket Drive on LinkedIn"
                data-testid="footer-linkedin-link"
                className="w-10 h-10 bg-slate-800 hover:bg-[#0A66C2] rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-700 mt-10 pt-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-gray-400">
              © {new Date().getFullYear()} Cricket Drive. All rights reserved.
            </p>
            <div className="flex space-x-6 text-sm text-gray-400">
              <a href="/privacy-policy" className="hover:text-green-400 transition-colors">
                Privacy Policy
              </a>
              <a href="/terms-of-service" className="hover:text-green-400 transition-colors">
                Terms of Service
              </a>
              <a href="/legal/data-deletion" className="hover:text-green-400 transition-colors" data-testid="footer-data-deletion-link">
                Data Deletion
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
